import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import { useCatalog } from "@/hooks/useCatalog";
import { useQuery } from "@tanstack/react-query";
import { Reveal, MaskLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import TrustBar from "@/components/TrustBar";
import { ProductCardSkeleton } from "@/components/Skeleton";
import { money, img, fetchReviews } from "@/lib/api";
import { LIFESTYLE, GIFT_SETS, RITUALS, STORY_CHAPTERS, QUALITY_STANDARDS } from "@/data/content";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    let l = document.getElementById("hero-preload");
    if (!l) { l = document.createElement("link"); l.id = "hero-preload"; l.rel = "preload"; l.as = "image"; document.head.appendChild(l); }
    l.href = LIFESTYLE.hero;
  }, []);

  return (
    <section ref={ref} className="relative grain min-h-[92vh] overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={LIFESTYLE.hero} alt="Small-batch honey in glass jars on a warm wooden table" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-wood-900/85 via-wood-900/50 to-wood-900/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-wood-900/50 to-transparent md:from-wood-900/30" />
      </motion.div>

      <div className="relative z-[2] mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-end px-5 pb-16 pt-32 md:px-8 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="mb-5 text-xs font-semibold uppercase tracking-[0.35em] text-honey-soft"
        >
          Family-rooted wellness · Northern California
        </motion.p>
        <h1 className="max-w-4xl font-serif text-5xl leading-[1.02] text-cream md:text-7xl lg:text-8xl">
          <MaskLine delay={0.25}>Everyday luxury,</MaskLine>
          <MaskLine delay={0.4} className="italic text-honey-soft">gently made.</MaskLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9 }}
          className="mt-6 max-w-xl text-lg text-cream/85"
        >
          Premium honey, loose-leaf tea, and botanical body care — made in small batches for the quiet, restorative rituals of everyday life.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9 }}
          className="mt-9 flex flex-wrap gap-3"
        >
          <Link to="/shop/honey" data-testid="hero-shop-honey" className="rounded-full bg-cream px-7 py-4 text-sm font-medium text-wood-900 transition-colors hover:bg-honey hover:text-white">Shop Honey</Link>
          <Link to="/shop/tea" data-testid="hero-shop-tea" className="rounded-full border border-cream/70 px-7 py-4 text-sm font-medium text-cream transition-colors hover:bg-cream hover:text-wood-900">Shop Tea</Link>
          <Link to="/shop/body-care" data-testid="hero-shop-body" className="rounded-full border border-cream/70 px-7 py-4 text-sm font-medium text-cream transition-colors hover:bg-cream hover:text-wood-900">Shop Body Care</Link>
          <Link to="/shop/gift-sets" data-testid="hero-shop-gifts" className="rounded-full border border-cream/70 px-7 py-4 text-sm font-medium text-cream transition-colors hover:bg-cream hover:text-wood-900">Shop Gift Sets</Link>
        </motion.div>
      </div>
    </section>
  );
}

function CategoryCards() {
  const cards = [
    { slug: "honey", label: "Honey", img: LIFESTYLE.honey },
    { slug: "tea", label: "Loose-Leaf Tea", img: LIFESTYLE.teaRitual },
    { slug: "body-care", label: "Body Care", img: LIFESTYLE.bodyOil },
    { slug: "gift-sets", label: "Gift Sets", img: LIFESTYLE.cozy },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="category-cards">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Find your ritual</p>
        <h2 className="mt-3 max-w-2xl font-serif text-4xl text-wood-900 md:text-5xl">Shop by collection</h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        {cards.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.08}>
            <Link to={`/shop/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group block">
              <div className="overflow-hidden rounded-t-[40%] rounded-b-2xl bg-sand">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={c.img} alt={c.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between px-1">
                <h3 className="font-serif text-xl text-wood-900 md:text-2xl">{c.label}</h3>
                <ArrowRight size={18} className="text-honey-dark transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ManukaFeature({ product }) {
  if (!product) return null;
  return (
    <section className="bg-sand" data-testid="manuka-feature">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-24 md:grid-cols-2 md:items-center md:gap-16 md:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-white">
            <img src={img(product.images[0], 1000)} alt={product.name} className="h-full w-full object-cover" />
            <span className="absolute left-4 top-4 rounded-full bg-wood-900 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-cream">MGO 234+</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">Featured · Documented rating</p>
          <h2 className="mt-3 font-serif text-4xl leading-tight text-wood-900 md:text-6xl">{product.name}</h2>
          <p className="mt-5 max-w-lg text-wood-700">{product.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <span className="font-serif text-3xl text-wood-900">
              {product.price_min === product.price_max ? money(product.price_min) : `${money(product.price_min)} – ${money(product.price_max)}`}
            </span>
            <span className="text-sm text-wood-500">
              {product.variations.map((v) => v.name).filter(Boolean).join(" · ")}
            </span>
          </div>
          <Link to={`/product/${product.slug}`} data-testid="manuka-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-wood-900 px-8 py-4 text-base font-medium text-cream transition-colors hover:bg-honey-dark">
            Shop Manuka Honey <ArrowRight size={16} />
          </Link>
          <p className="mt-4 text-sm text-wood-500">
            <Link to={`/product/${product.slug}`} className="text-honey-dark underline underline-offset-2">What does MGO 234+ mean?</Link> — learn about the grade and how to enjoy it.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function RitualSplit() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="rituals">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Simple daily moments</p>
        <h2 className="mt-3 max-w-2xl font-serif text-4xl text-wood-900 md:text-5xl">Rituals worth slowing down for</h2>
      </Reveal>
      <div className="mt-14 flex flex-col gap-16 md:gap-24">
        {RITUALS.map((r, i) => (
          <Reveal key={r.id}>
            <div className={`grid items-center gap-8 md:grid-cols-12 md:gap-14 ${i % 2 ? "md:[direction:rtl]" : ""}`}>
              <div className="md:col-span-7 md:[direction:ltr]">
                <div className="overflow-hidden rounded-3xl">
                  <img src={LIFESTYLE[r.image]} alt={r.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </div>
              <div className="md:col-span-5 md:[direction:ltr]">
                <span className="font-serif text-5xl text-honey">{r.number}</span>
                <h3 className="mt-3 font-serif text-3xl text-wood-900 md:text-4xl">{r.title}</h3>
                <p className="mt-4 max-w-md text-wood-700">{r.body}</p>
                <Link to={`/shop/${r.slug}`} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-honey-dark hover:gap-3 transition-all">
                  Explore <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GiftSets({ bySlug }) {
  return (
    <section className="bg-sage-pale" data-testid="gift-sets">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Thoughtful to give</p>
          <h2 className="mt-3 max-w-2xl font-serif text-4xl text-wood-900 md:text-5xl">Curated gift sets</h2>
          <p className="mt-4 max-w-xl text-wood-700">Hand-picked pairings of our real products, ready to gift — or to keep for yourself.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {GIFT_SETS.map((set, i) => {
            const products = set.slugs.map(bySlug).filter(Boolean);
            return (
              <Reveal key={set.id} delay={i * 0.06}>
                <Link to={`/shop/gift-sets`} data-testid={`gift-set-${set.id}`} className="group flex h-full flex-col rounded-3xl bg-cream p-6 transition-shadow hover:shadow-[0_12px_40px_rgba(62,49,40,0.1)]">
                  <div className="flex -space-x-6">
                    {products.slice(0, 3).map((p) => (
                      <div key={p.id} className="h-28 w-24 overflow-hidden rounded-2xl border-4 border-cream bg-sand">
                        <img src={img(p.images[0], 300)} alt={p.name} className="h-full w-full object-cover" />
                      </div>
                    ))}
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-wood-900">{set.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-wood-700">{set.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-honey-dark">
                    View the set <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FamilyStory() {
  return (
    <section className="relative overflow-hidden bg-wood-900 text-cream" data-testid="family-story">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 md:grid-cols-2 md:items-center md:gap-16 md:px-8">
        <Reveal>
          <div className="overflow-hidden rounded-3xl">
            <img src={LIFESTYLE.family} alt="A warm family moment outdoors in natural light" className="aspect-[4/5] w-full object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-soft">Our story</p>
            <h2 className="mt-3 font-serif text-4xl leading-tight md:text-5xl">A family, and a table worth gathering around</h2>
          </Reveal>
          <div className="mt-8 space-y-7">
            {STORY_CHAPTERS.map((c, i) => (
              <Reveal key={c.number} delay={i * 0.08}>
                <div className="flex gap-5 border-t border-cream/15 pt-6">
                  <span className="font-serif text-3xl text-honey">{c.number}</span>
                  <div>
                    <h3 className="font-serif text-2xl">{c.title}</h3>
                    <p className="mt-2 text-cream/75">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/our-story" className="mt-8 inline-flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3.5 text-sm font-medium text-cream transition-colors hover:bg-cream hover:text-wood-900">
              Read our full story <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Quality() {
  return (
    <section className="bg-sage text-cream" data-testid="quality-standards">
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <Reveal>
          <h2 className="max-w-2xl font-serif text-4xl md:text-5xl">Our quality & ingredient standards</h2>
        </Reveal>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {QUALITY_STANDARDS.map((q, i) => (
            <Reveal key={q.title} delay={i * 0.07}>
              <div className="border-t border-cream/25 pt-5">
                <h3 className="font-serif text-2xl">{q.title}</h3>
                <p className="mt-2 text-sm text-cream/80">{q.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const { data } = useQuery({ queryKey: ["reviews", "all"], queryFn: () => fetchReviews() });
  const reviews = data?.reviews || [];
  const hasReviews = reviews.length > 0;
  return (
    <section className="mx-auto max-w-5xl px-5 py-24 text-center md:px-8" data-testid="reviews">
      <Reveal>
        <div className="flex justify-center gap-1 text-honey">
          {[...Array(5)].map((_, i) => <Star key={i} size={18} fill={hasReviews && i < Math.round(data.average) ? "currentColor" : "none"} />)}
        </div>
        {hasReviews ? (
          <>
            <h2 className="mt-6 font-serif text-4xl text-wood-900 md:text-5xl">Loved by our growing family</h2>
            <p className="mt-3 text-lg text-wood-700">{data.average} average · {data.count} verified review{data.count > 1 ? "s" : ""}</p>
            <div className="mt-10 grid gap-6 text-left md:grid-cols-3">
              {reviews.slice(0, 3).map((r) => (
                <div key={r.id} className="rounded-2xl border border-wood-900/10 bg-white p-6">
                  <div className="flex gap-0.5 text-honey">{[...Array(r.rating)].map((_, i) => <Star key={i} size={15} fill="currentColor" />)}</div>
                  {r.title && <h3 className="mt-3 font-serif text-lg text-wood-900">{r.title}</h3>}
                  <p className="mt-2 text-base text-wood-700">{r.body}</p>
                  <p className="mt-3 text-sm text-wood-500">— {r.name} · Verified buyer</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h2 className="mt-6 font-serif text-4xl text-wood-900 md:text-5xl">Loved by our growing family</h2>
            <p className="mx-auto mt-5 max-w-xl text-lg text-wood-700">
              We only show verified reviews from real customers — so this space is reserved for genuine words as our first orders arrive.
            </p>
            <Link to="/contact" className="mt-8 inline-flex rounded-full border border-wood-900/30 px-7 py-3.5 text-base font-medium text-wood-900 transition-colors hover:bg-wood-900 hover:text-cream">
              Share your experience
            </Link>
          </>
        )}
      </Reveal>
    </section>
  );
}

function QuizCTA() {
  return (
    <section className="bg-honey/15" data-testid="quiz-cta">
      <div className="mx-auto max-w-5xl px-5 py-20 text-center md:px-8">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-cream px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-honey-dark">
            <Sparkles size={14} /> 3 quick questions
          </span>
          <h2 className="mt-5 font-serif text-4xl text-wood-900 md:text-5xl">Find your self-care ritual</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-wood-700">
            A calm, guided way to discover the honey, tea, or body-care ritual that fits you — or a thoughtful gift for someone special.
          </p>
          <Link to="/quiz" data-testid="home-quiz-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-wood-900 px-9 py-4 text-base font-medium text-cream transition-colors hover:bg-honey-dark">
            Take the quiz <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { byCategory, bySlug, isLoading } = useCatalog();
  const manuka = bySlug("authentic-new-zealand-manuka-honey-mgo-234");
  const bestSellers = byCategory("honey").slice(0, 4);

  return (
    <div>
      <Hero />
      <Marquee items={["Natural Wellness", "Premium Manuka Honey MGO 234+", "Small-Batch Teas", "Restorative Rituals"]} />
      <CategoryCards />
      <ManukaFeature product={manuka} />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="home-honey-grid">
        <Reveal>
          <div className="flex items-end justify-between">
            <h2 className="max-w-xl font-serif text-4xl text-wood-900 md:text-5xl">From the honey pantry</h2>
            <Link to="/shop/honey" className="hidden items-center gap-2 text-base font-semibold text-honey-dark md:inline-flex">
              View all <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">
          {isLoading
            ? [...Array(4)].map((_, i) => <ProductCardSkeleton key={i} />)
            : bestSellers.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}><ProductCard product={p} /></Reveal>
              ))}
        </div>
      </section>

      <QuizCTA />
      <RitualSplit />
      <GiftSets bySlug={bySlug} />
      <FamilyStory />
      <Quality />
      <TrustBar />
      <Reviews />
    </div>
  );
}
